import React, { useState, useEffect } from 'react';
import { Header } from '../components/Header';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '../components/ui/alert-dialog';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import { toast } from 'sonner';
import { adminAPI, categoryAPI } from '../lib/api';
import { formatCurrency, formatDate } from '../lib/utils';
import {
  Plus,
  Trash2,
  Loader2,
  Users,
  Eye,
  Shield,
  Fuel,
  UtensilsCrossed,
  Wrench,
  PenLine,
  Film,
  Car,
  Zap,
  Folder,
} from 'lucide-react';

const iconOptions = [
  { value: 'fuel', label: 'Fuel', icon: Fuel },
  { value: 'utensils', label: 'Food', icon: UtensilsCrossed },
  { value: 'wrench', label: 'Equipment', icon: Wrench },
  { value: 'pen', label: 'Stationary', icon: PenLine },
  { value: 'film', label: 'Entertainment', icon: Film },
  { value: 'car', label: 'Transport', icon: Car },
  { value: 'zap', label: 'Utilities', icon: Zap },
  { value: 'folder', label: 'Other', icon: Folder },
];

export default function AdminPage() {
  const [users, setUsers] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedUser, setSelectedUser] = useState(null);
  const [userExpenses, setUserExpenses] = useState([]);
  const [loadingExpenses, setLoadingExpenses] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [formData, setFormData] = useState({ name: '', icon: 'folder' });
  const [saving, setSaving] = useState(false);

  const fetchData = async () => {
    try {
      const [usersRes, categoriesRes] = await Promise.all([
        adminAPI.getUsers(),
        adminAPI.getDefaultCategories(),
      ]);
      setUsers(usersRes.data);
      setCategories(categoriesRes.data);
    } catch (error) {
      toast.error('Failed to load admin data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const handleViewUserExpenses = async (user) => {
    setSelectedUser(user);
    setLoadingExpenses(true);
    try {
      const response = await adminAPI.getUserExpenses(user.id);
      setUserExpenses(response.data);
    } catch (error) {
      toast.error('Failed to load user expenses');
    } finally {
      setLoadingExpenses(false);
    }
  };

  const handleDeleteUser = async (userId) => {
    try {
      await adminAPI.deleteUser(userId);
      toast.success('User deleted');
      setUsers(users.filter((u) => u.id !== userId));
      if (selectedUser?.id === userId) {
        setSelectedUser(null);
        setUserExpenses([]);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete user');
    }
  };

  const handleAddCategory = async () => {
    if (!formData.name.trim()) {
      toast.error('Please enter a category name');
      return;
    }

    setSaving(true);
    try {
      await adminAPI.createDefaultCategory(formData);
      toast.success('Default category created');
      setDialogOpen(false);
      setFormData({ name: '', icon: 'folder' });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create category');
    } finally {
      setSaving(false);
    }
  };

  const getIconComponent = (iconName) => {
    const option = iconOptions.find((o) => o.value === iconName);
    return option?.icon || Folder;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <div className="flex items-center justify-center h-[calc(100vh-64px)]">
          <Loader2 className="w-8 h-8 animate-spin text-primary" />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background" data-testid="admin-page">
      <Header />
      <main className="container mx-auto px-4 py-6 max-w-6xl">
        <div className="space-y-6">
          <div className="flex items-center gap-3">
            <Shield className="w-8 h-8 text-primary" />
            <div>
              <h1 className="text-2xl font-bold tracking-tight">Super Admin Panel</h1>
              <p className="text-muted-foreground">Manage users and default categories</p>
            </div>
          </div>

          <div className="grid lg:grid-cols-2 gap-6">
            {/* Users Card */}
            <Card className="bg-card border-white/10">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Users className="w-5 h-5" />
                  Users ({users.length})
                </CardTitle>
                <CardDescription>Manage all registered users</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-2 max-h-[400px] overflow-y-auto">
                  {users.map((user) => (
                    <div
                      key={user.id}
                      className={`flex items-center justify-between p-3 rounded-lg border transition-colors cursor-pointer ${
                        selectedUser?.id === user.id
                          ? 'bg-primary/10 border-primary/30'
                          : 'bg-secondary/30 border-white/5 hover:border-white/10'
                      }`}
                      onClick={() => handleViewUserExpenses(user)}
                      data-testid={`user-${user.id}`}
                    >
                      <div>
                        <p className="font-medium">{user.name}</p>
                        <p className="text-sm text-muted-foreground">{user.email}</p>
                      </div>
                      <div className="flex items-center gap-2">
                        {user.role === 'super_admin' && (
                          <span className="text-xs bg-primary/20 text-primary px-2 py-1 rounded-full">
                            Super Admin
                          </span>
                        )}
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-8 w-8 hover:bg-white/5"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleViewUserExpenses(user);
                          }}
                        >
                          <Eye className="w-4 h-4" />
                        </Button>
                        {user.role !== 'super_admin' && (
                          <AlertDialog>
                            <AlertDialogTrigger asChild>
                              <Button
                                variant="ghost"
                                size="icon"
                                className="h-8 w-8 text-destructive hover:bg-destructive/10"
                                onClick={(e) => e.stopPropagation()}
                                data-testid={`delete-user-${user.id}`}
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </AlertDialogTrigger>
                            <AlertDialogContent className="bg-card border-white/10">
                              <AlertDialogHeader>
                                <AlertDialogTitle>Delete user?</AlertDialogTitle>
                                <AlertDialogDescription>
                                  This will permanently delete {user.name} and all their data.
                                  This action cannot be undone.
                                </AlertDialogDescription>
                              </AlertDialogHeader>
                              <AlertDialogFooter>
                                <AlertDialogCancel className="bg-secondary border-white/10">
                                  Cancel
                                </AlertDialogCancel>
                                <AlertDialogAction
                                  onClick={() => handleDeleteUser(user.id)}
                                  className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                                >
                                  Delete
                                </AlertDialogAction>
                              </AlertDialogFooter>
                            </AlertDialogContent>
                          </AlertDialog>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>

            {/* Default Categories Card */}
            <Card className="bg-card border-white/10">
              <CardHeader className="flex flex-row items-center justify-between">
                <div>
                  <CardTitle>Default Categories</CardTitle>
                  <CardDescription>Categories available to all users</CardDescription>
                </div>
                <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
                  <DialogTrigger asChild>
                    <Button
                      className="bg-primary text-primary-foreground hover:bg-primary/90 rounded-full"
                      data-testid="add-default-category-btn"
                    >
                      <Plus className="w-4 h-4 mr-2" />
                      Add
                    </Button>
                  </DialogTrigger>
                  <DialogContent className="bg-card border-white/10">
                    <DialogHeader>
                      <DialogTitle>New Default Category</DialogTitle>
                      <DialogDescription>
                        This category will be available to all users
                      </DialogDescription>
                    </DialogHeader>
                    <div className="space-y-4 py-4">
                      <div className="space-y-2">
                        <Label htmlFor="name">Name</Label>
                        <Input
                          id="name"
                          value={formData.name}
                          onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                          placeholder="Category name"
                          className="bg-input/50 border-white/10"
                          data-testid="default-category-name-input"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>Icon</Label>
                        <Select
                          value={formData.icon}
                          onValueChange={(value) => setFormData({ ...formData, icon: value })}
                        >
                          <SelectTrigger className="bg-input/50 border-white/10">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent className="bg-card border-white/10">
                            {iconOptions.map((option) => {
                              const IconComp = option.icon;
                              return (
                                <SelectItem key={option.value} value={option.value}>
                                  <div className="flex items-center gap-2">
                                    <IconComp className="w-4 h-4" />
                                    {option.label}
                                  </div>
                                </SelectItem>
                              );
                            })}
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                    <DialogFooter>
                      <Button
                        variant="ghost"
                        onClick={() => setDialogOpen(false)}
                        className="border-white/10"
                      >
                        Cancel
                      </Button>
                      <Button
                        onClick={handleAddCategory}
                        disabled={saving}
                        className="bg-primary text-primary-foreground hover:bg-primary/90"
                        data-testid="save-default-category-btn"
                      >
                        {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                        Save
                      </Button>
                    </DialogFooter>
                  </DialogContent>
                </Dialog>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  {categories.map((category) => {
                    const IconComp = getIconComponent(category.icon);
                    return (
                      <div
                        key={category.id}
                        className="flex items-center justify-between p-3 rounded-lg bg-secondary/30 border border-white/5"
                      >
                        <div className="flex items-center gap-3">
                          <div className="w-8 h-8 rounded-full bg-white/5 flex items-center justify-center">
                            <IconComp className="w-4 h-4 text-muted-foreground" />
                          </div>
                          <span className="font-medium">{category.name}</span>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Selected User Expenses */}
          {selectedUser && (
            <Card className="bg-card border-white/10">
              <CardHeader>
                <CardTitle>{selectedUser.name}'s Expenses</CardTitle>
                <CardDescription>{selectedUser.email}</CardDescription>
              </CardHeader>
              <CardContent>
                {loadingExpenses ? (
                  <div className="flex items-center justify-center py-8">
                    <Loader2 className="w-6 h-6 animate-spin text-primary" />
                  </div>
                ) : userExpenses.length === 0 ? (
                  <p className="text-center py-8 text-muted-foreground">No expenses found</p>
                ) : (
                  <Table>
                    <TableHeader>
                      <TableRow className="border-white/10">
                        <TableHead>Date</TableHead>
                        <TableHead>Description</TableHead>
                        <TableHead>Category</TableHead>
                        <TableHead className="text-right">Amount</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {userExpenses.map((expense) => (
                        <TableRow key={expense.id} className="border-white/10">
                          <TableCell>{formatDate(expense.date)}</TableCell>
                          <TableCell>{expense.description}</TableCell>
                          <TableCell>{expense.category_name}</TableCell>
                          <TableCell className="text-right font-medium tabular-nums text-red-400">
                            -{formatCurrency(expense.amount)}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )}
              </CardContent>
            </Card>
          )}
        </div>
      </main>
    </div>
  );
}
